import { useContext, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import { useAgent } from "@watermarkinsights/ai-toolbox";
import { WmSnackbar } from "@watermarkinsights/ripple-react";
import { WmSnackbarCustomEvent } from "@watermarkinsights/ripple";
import { AppContext, AppContextInterface } from "@/contexts/AppContext";
import { notifications } from "@/features/common/interfaces/Interface";
import { trapTabFocus } from "../../../../static/js/commons";
import {
  buildEndMessageSourceProductsByMessageId,
  buildExportableMessages,
  buildMessagePairs,
  deriveVisibleMessages,
  extractTextFromMessageContent,
} from "../ai-chat-message-utils";
import type { AgentMessage } from "../ai-chat-message-utils";
import DraftConversationPanel from "../draft-message/DraftConversationPanel";
import ExportConversationPanel from "../export-conversation/ExportConversationPanel";
import { useChatPanelState } from "./useChatPanelState";
import { usePanelResize } from "./usePanelResize";
import { useJumpToLatest } from "./useJumpToLatest";
import { useQuotaStatus } from "../useQuotaStatus";
import { ChatHeader } from "./ChatHeader";
import { ChatMessageList } from "./ChatMessageList";
import { ChatFooter } from "./ChatFooter";
import { ClearConversationModal } from "./ClearConversationModal";

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  pendingMessage: string | null;
  onPendingMessageHandled: () => void;
}

export function ChatPanel({
  isOpen,
  onClose,
  pendingMessage,
  onPendingMessageHandled,
}: ChatPanelProps) {
  const intl = useIntl();
  const { sendMessage, isRunning, messages } = useAgent();
  const { setChatStreaming, setChatQuotaExhausted, isAiAssistantAvailable, institution } =
    useContext<AppContextInterface>(AppContext);
  const isAiEnabled = isAiAssistantAvailable;
  const chatPanel = useChatPanelState();
  const quota = useQuotaStatus(isOpen);
  const {
    inputText,
    isAwaitingAgentResponse,
    panelWidthInPixels,
    isClearConversationModalOpen,
    clearedMessageCount,
    view,
    isDraftResultView,
    backToSelectionToken,
    tableSortByMessageId,
  } = chatPanel;
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLElement>(null);

  const { handleResizeStart } = usePanelResize(
    panelWidthInPixels,
    chatPanel.setPanelWidthInPixels,
  );

  useEffect(() => {
    if (isOpen) {
      textareaRef.current?.focus();
    }
  }, [isOpen]);

  const handlePanelKeyDown = (e: React.KeyboardEvent) => {
    trapTabFocus(panelRef.current, e);
  };

  useEffect(() => {
    if (pendingMessage && quota.initialized && !isAwaitingAgentResponse) {
      if (quota.canSend) {
        sendMessage(pendingMessage);
        quota.recordUsage();
        chatPanel.setIsAwaitingAgentResponse(true);
      }
      onPendingMessageHandled();
    }
  }, [pendingMessage, quota.initialized]);

  const visibleMessages = deriveVisibleMessages(messages, clearedMessageCount);
  const sourceProductsByMessageId = buildEndMessageSourceProductsByMessageId(
    messages,
    isRunning,
  );
  const isIntroState = visibleMessages.length === 0 && !isRunning;

  const latestUserMessageId =
    [...visibleMessages].reverse().find((m) => m.role === "user")?.id ?? null;

  const messagePairs = buildMessagePairs(visibleMessages);
  const exportableMessages = buildExportableMessages(visibleMessages);

  const lastVisibleMessage = visibleMessages[visibleMessages.length - 1];
  const lastAssistantMessage =
    lastVisibleMessage?.role === "assistant"
      ? (lastVisibleMessage as Extract<AgentMessage, { role: "assistant" }>)
      : null;
  const lastMessageIsAssistantWithText =
    lastAssistantMessage !== null &&
    (extractTextFromMessageContent(lastAssistantMessage.content).trim() !== "" ||
      (!isRunning && (lastAssistantMessage.toolCalls?.length ?? 0) > 0));

  useEffect(() => {
    if (isAwaitingAgentResponse && lastMessageIsAssistantWithText) {
      chatPanel.setIsAwaitingAgentResponse(false);
    }
  }, [isAwaitingAgentResponse, lastMessageIsAssistantWithText]);

  const isQuotaExhausted = quota.initialized && !quota.allowed;

  useEffect(() => {
    setChatQuotaExhausted(isQuotaExhausted);
  }, [isQuotaExhausted]);

  useEffect(() => {
    setChatStreaming(isAwaitingAgentResponse || isRunning);
  }, [isAwaitingAgentResponse, isRunning]);

  const {
    isJumpToLatestVisible,
    messagesContainerRef,
    latestExchangeAnchorRef,
    messagesEndRef,
    updateJumpToLatestVisibility,
    handleJumpToLatest,
  } = useJumpToLatest(visibleMessages, isRunning, view);

  const shouldShowTypingIndicator =
    isAwaitingAgentResponse && !lastMessageIsAssistantWithText;

  const handleSuggestedQuestionSelected = (question: string) => {
    if (isAwaitingAgentResponse || isRunning || !quota.canSend) return;
    sendMessage(question);
    quota.recordUsage();
    chatPanel.setIsAwaitingAgentResponse(true);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedInput = inputText.trim();
    if (!trimmedInput || isAwaitingAgentResponse || !quota.canSend) return;
    sendMessage(trimmedInput);
    quota.recordUsage();
    chatPanel.setInputText("");
    chatPanel.setIsAwaitingAgentResponse(true);
    textareaRef.current?.focus();
  };

  const handleTextareaKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleFormSubmit(e as unknown as React.FormEvent);
    }
  };

  const handleConfirmClearConversation = () => {
    chatPanel.confirmClearConversation(messages.length);
  };

  let exportNotifications: notifications[] = [];

  const showExportSuccessSnackbar = () => {
    const snackbar: any = document.getElementById("wm-snackbar-export");
    const newNotification = {
      id: `snack-${Math.floor(Math.random() * 100) + 1}`,
      message: intl.formatMessage({
        id: "app.toolboxChat.exportConversation.successMessage",
      }),
      link: "",
    };
    exportNotifications = [newNotification, ...exportNotifications];
    snackbar.notifications = JSON.stringify(exportNotifications);
  };

  const dismissExportSnackbar = (ev: WmSnackbarCustomEvent<Object>) => {
    const snackbar: any = document.getElementById(ev.target.id);
    exportNotifications = exportNotifications.filter(
      (snack: notifications) => snack.id !== (ev.detail as any).id,
    );
    snackbar.notifications = JSON.stringify(exportNotifications);
  };

  return (
    <aside
      ref={panelRef}
      className={`tcw-panel${isOpen ? " is-open" : ""}`}
      aria-label={intl.formatMessage({ id: "app.toolboxChat.title" })}
      onKeyDown={handlePanelKeyDown}
    >
      <div className="tcw-resize-handle" onMouseDown={handleResizeStart} />

      <div
        className={`tcw-header${
          view === "draft" || view === "export" ? " tcw-header--draft" : ""
        }${view === "chat" && isIntroState ? " tcw-header--no-border" : ""}`}
      >
        <ChatHeader
          view={view}
          isDraftResultView={isDraftResultView}
          onClose={onClose}
          onBackToConversation={chatPanel.closeDraftPanel}
          onBackToSelection={chatPanel.requestBackToSelection}
          onClearConversation={chatPanel.openClearConversationModal}
          onOpenDraftPanel={chatPanel.openDraftPanel}
          onOpenExportPanel={chatPanel.openExportPanel}
          onCloseExportPanel={chatPanel.closeExportPanel}
          isAiEnabled={isAiEnabled}
        />
      </div>

      {view === "draft" ? (
        <div className="tcw-messages tcw-messages--draft">
          <DraftConversationPanel
            messagePairs={messagePairs}
            onBackToConversation={chatPanel.closeDraftPanel}
            resetSignal={backToSelectionToken}
            onResultStateChange={chatPanel.setIsDraftResultView}
          />
        </div>
      ) : view === "export" ? (
        <div className="tcw-messages tcw-messages--export">
          <ExportConversationPanel
            messages={exportableMessages}
            institutionName={institution?.name ?? ""}
            tableSortByMessageId={tableSortByMessageId}
            onTableSortChange={chatPanel.setTableSort}
            onBackToConversation={chatPanel.closeExportPanel}
            onExportSuccess={showExportSuccessSnackbar}
          />
        </div>
      ) : (
        <ChatMessageList
          isIntroState={isIntroState}
          visibleMessages={visibleMessages}
          sourceProductsByMessageId={sourceProductsByMessageId}
          tableSortByMessageId={tableSortByMessageId}
          onTableSortChange={chatPanel.setTableSort}
          latestUserMessageId={latestUserMessageId}
          isAwaitingAgentResponse={isAwaitingAgentResponse}
          isRunning={isRunning}
          canSend={quota.canSend}
          shouldShowTypingIndicator={shouldShowTypingIndicator}
          isJumpToLatestVisible={isJumpToLatestVisible}
          messagesContainerRef={messagesContainerRef}
          latestExchangeAnchorRef={latestExchangeAnchorRef}
          messagesEndRef={messagesEndRef}
          onScroll={updateJumpToLatestVisibility}
          onQuestionSelected={quota.canSend ? handleSuggestedQuestionSelected : undefined}
          onJumpToLatest={handleJumpToLatest}
        />
      )}

      <ChatFooter
        view={view}
        inputText={inputText}
        isRunning={isRunning}
        textareaRef={textareaRef}
        onInputChange={chatPanel.setInputText}
        onSubmit={handleFormSubmit}
        onKeyDown={handleTextareaKeyDown}
        canSend={quota.canSend}
        initialized={quota.initialized}
        reason={quota.reason}
        quotaError={quota.error}
      />

      <ClearConversationModal
        isOpen={isClearConversationModalOpen}
        onCancel={chatPanel.closeClearConversationModal}
        onConfirm={handleConfirmClearConversation}
      />

      <WmSnackbar
        notifications="[]"
        id="wm-snackbar-export"
        onWmSnackbarSnackFinished={(event) => dismissExportSnackbar(event)}
      />
    </aside>
  );
}
